/* Copyright (c) 2026 Fluxonix Inc. */

import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import {
  createPilotKpi,
  deletePilotKpi,
  getOperationsCenterSummary,
  updatePilotKpi,
} from "../api/operationsCenter";

export default function OperationsCenter() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);

  async function loadSummary() {
    try {
      setLoading(true);
      setData(await getOperationsCenterSummary());
    } catch (error) {
      console.error(error);
      toast.error("Mission Control could not be loaded");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSummary();
  }, []);

  const trackedKeys = useMemo(
    () => new Set((data?.kpis || []).map((kpi) => kpi.metric_key)),
    [data]
  );
  const availableMetrics = (data?.catalog || []).filter(
    (metric) => !trackedKeys.has(metric.key)
  );

  function openNewKpi() {
    const metric = availableMetrics[0];
    if (!metric) return;
    setEditor({
      id: null,
      metric_key: metric.key,
      name: metric.label,
      description: metric.description,
      baseline_value: metric.suggested_baseline ?? "",
      target_value: metric.suggested_target,
    });
  }

  function openEditKpi(kpi) {
    setEditor({
      id: kpi.id,
      metric_key: kpi.metric_key,
      name: kpi.name,
      description: kpi.description || "",
      baseline_value: kpi.baseline_value ?? "",
      target_value: kpi.target_value,
    });
  }

  function selectMetric(metricKey) {
    const metric = data.catalog.find((item) => item.key === metricKey);
    setEditor((current) => ({
      ...current,
      metric_key: metric.key,
      name: metric.label,
      description: metric.description,
      baseline_value: metric.suggested_baseline ?? "",
      target_value: metric.suggested_target,
    }));
  }

  async function saveKpi(event) {
    event.preventDefault();
    const payload = {
      metric_key: editor.metric_key,
      name: editor.name.trim(),
      description: editor.description.trim() || null,
      baseline_value:
        editor.baseline_value === "" ? null : Number(editor.baseline_value),
      target_value: Number(editor.target_value),
    };
    try {
      setSaving(true);
      if (editor.id) {
        await updatePilotKpi(editor.id, payload);
      } else {
        await createPilotKpi(payload);
      }
      toast.success(editor.id ? "Pilot KPI updated" : "Pilot KPI added");
      setEditor(null);
      await loadSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Pilot KPI could not be saved");
    } finally {
      setSaving(false);
    }
  }

  async function removeKpi(kpi) {
    if (!window.confirm(`Stop tracking “${kpi.name}”?`)) return;
    try {
      await deletePilotKpi(kpi.id);
      toast.success("Pilot KPI removed");
      await loadSummary();
    } catch (error) {
      toast.error("Pilot KPI could not be removed");
    }
  }

  if (loading && !data) {
    return <LoadingState />;
  }
  if (!data) return null;

  const snapshot = data.operational_snapshot || {};
  const canManage = hasRole("admin");

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8 lg:px-10">
      <div className="mx-auto max-w-7xl space-y-8">
        <header className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">
              Pilot outcomes
            </p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
              Mission Control
            </h1>
            <p className="mt-2 max-w-3xl text-slate-600">
              Define the quality outcomes that matter to your team. Fluxonix updates
              each measurement from incidents, reviews, and corrective actions as work
              moves through the pilot.
            </p>
          </div>
          {canManage && availableMetrics.length > 0 && (
            <button
              onClick={openNewKpi}
              className="rounded-xl bg-blue-700 px-5 py-3 text-sm font-bold text-white shadow-sm hover:bg-blue-800"
            >
              Add pilot KPI
            </button>
          )}
        </header>

        <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <SnapshotCard label="Incidents captured" value={snapshot.total_incidents} />
          <SnapshotCard label="Open incidents" value={snapshot.open_incidents} />
          <SnapshotCard label="Pending reviews" value={snapshot.pending_reviews} />
          <SnapshotCard label="Open actions" value={snapshot.open_actions} />
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-950">Pilot scorecard</h2>
              <p className="mt-1 text-sm text-slate-500">
                Baseline, target, and current values are kept together so pilot success
                can be evaluated with shared evidence.
              </p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
              {data.kpis.length} tracked
            </span>
          </div>

          {data.kpis.length === 0 ? (
            <EmptyScorecard canManage={canManage} onAdd={openNewKpi} />
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-2">
              {data.kpis.map((kpi) => (
                <KpiCard
                  key={kpi.id}
                  kpi={kpi}
                  canManage={canManage}
                  onEdit={() => openEditKpi(kpi)}
                  onRemove={() => removeKpi(kpi)}
                />
              ))}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div>
            <h2 className="text-xl font-bold text-slate-950">Recent incidents</h2>
            <p className="mt-1 text-sm text-slate-500">
              Each incident updates the applicable pilot measurements automatically.
            </p>
          </div>
          {data.recent_incidents.length === 0 ? (
            <p className="mt-6 rounded-xl bg-slate-50 p-5 text-sm text-slate-500">
              No live incidents have been captured yet.
            </p>
          ) : (
            <div className="mt-5 divide-y divide-slate-100">
              {data.recent_incidents.map((incident) => (
                <button
                  key={incident.id}
                  onClick={() => navigate(`/secure/incidents/${incident.id}`)}
                  className="flex w-full items-center justify-between gap-4 py-4 text-left hover:bg-slate-50"
                >
                  <div>
                    <p className="font-bold text-slate-900">{incident.incident_number}</p>
                    <p className="mt-1 text-sm text-slate-500">{incident.title}</p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
                    {formatStatus(incident.status)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>

        <TelemetryDisclosure disclosure={data.telemetry_disclosure} />
      </div>

      {editor && (
        <KpiEditor
          editor={editor}
          catalog={editor.id ? data.catalog : availableMetrics}
          saving={saving}
          onChange={setEditor}
          onMetricChange={selectMetric}
          onClose={() => setEditor(null)}
          onSubmit={saveKpi}
        />
      )}
    </div>
  );
}

function TelemetryDisclosure({ disclosure }) {
  if (!disclosure) return null;
  return (
    <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">
        Privacy-safe pilot telemetry
      </p>
      <h2 className="mt-2 text-xl font-bold text-slate-950">You can see exactly what Fluxonix receives</h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-600">
        Fluxonix receives only product milestones and aggregate pilot measurements. Your incident
        content stays inside your tenant.
      </p>
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <DisclosureList title="Shared with Fluxonix" items={disclosure.shared} tone="shared" />
        <DisclosureList title="Never shared through telemetry" items={disclosure.never_shared} tone="private" />
      </div>
    </section>
  );
}

function DisclosureList({ title, items = [], tone }) {
  return (
    <div className="rounded-xl border border-white/80 bg-white p-5">
      <h3 className="font-bold text-slate-900">{title}</h3>
      <ul className="mt-3 space-y-2 text-sm text-slate-600">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <span className={tone === "shared" ? "text-emerald-600" : "text-slate-400"}>✓</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8">
      <div className="mx-auto max-w-7xl rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
        Loading pilot outcomes…
      </div>
    </div>
  );
}

function SnapshotCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-2 text-3xl font-black text-slate-950">{value ?? 0}</p>
    </div>
  );
}

function EmptyScorecard({ canManage, onAdd }) {
  return (
    <div className="mt-6 rounded-2xl border border-dashed border-blue-200 bg-blue-50 p-8 text-center">
      <h3 className="font-bold text-slate-950">Agree on pilot success before measuring it</h3>
      <p className="mx-auto mt-2 max-w-2xl text-sm text-slate-600">
        Select the operational measurements your team already cares about, enter the
        pre-pilot baseline, and define the result you want Fluxonix to help achieve.
      </p>
      {canManage && (
        <button onClick={onAdd} className="mt-5 rounded-xl bg-blue-700 px-5 py-3 text-sm font-bold text-white">
          Define the first KPI
        </button>
      )}
    </div>
  );
}

function KpiCard({ kpi, canManage, onEdit, onRemove }) {
  const symbol = kpi.target_direction === "at_least" ? "≥" : "≤";
  return (
    <article className="rounded-2xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-bold text-slate-950">{kpi.name}</h3>
            <StatusBadge status={kpi.status} />
          </div>
          <p className="mt-2 text-sm text-slate-500">{kpi.description}</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <button onClick={onEdit} className="text-sm font-bold text-blue-700">Edit</button>
            <button onClick={onRemove} className="text-sm font-bold text-slate-400">Remove</button>
          </div>
        )}
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3 rounded-xl bg-slate-50 p-4">
        <Value label="Baseline" value={formatValue(kpi.baseline_value, kpi.unit)} />
        <Value label="Current" value={formatValue(kpi.current_value, kpi.unit)} strong />
        <Value label="Target" value={`${symbol} ${formatValue(kpi.target_value, kpi.unit)}`} />
      </div>
      {kpi.progress_percent !== null && (
        <div className="mt-4">
          <div className="flex justify-between text-xs font-bold text-slate-500">
            <span>Progress from baseline</span><span>{kpi.progress_percent}%</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-blue-600" style={{ width: `${kpi.progress_percent}%` }} />
          </div>
        </div>
      )}
      <p className="mt-4 text-xs text-slate-400">
        Based on {kpi.sample_size} {kpi.sample_size === 1 ? "record" : "records"} in Fluxonix
      </p>
    </article>
  );
}

function Value({ label, value, strong = false }) {
  return <div><p className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</p><p className={`mt-1 ${strong ? "text-xl font-black text-blue-700" : "font-bold text-slate-800"}`}>{value}</p></div>;
}

function StatusBadge({ status }) {
  const styles = status === "achieved" ? "bg-emerald-100 text-emerald-700" : status === "collecting_data" ? "bg-slate-100 text-slate-600" : "bg-blue-100 text-blue-700";
  const label = status === "achieved" ? "Target met" : status === "collecting_data" ? "Collecting data" : "In progress";
  return <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${styles}`}>{label}</span>;
}

function KpiEditor({ editor, catalog, saving, onChange, onMetricChange, onClose, onSubmit }) {
  const selected = catalog.find((metric) => metric.key === editor.metric_key) || catalog[0];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="text-xl font-black text-slate-950">{editor.id ? "Edit pilot KPI" : "Add pilot KPI"}</h2>
        <p className="mt-1 text-sm text-slate-500">Current values are calculated from Fluxonix workflow records.</p>
        {!editor.id && (
          <label className="mt-6 block text-sm font-bold text-slate-700">Measurement
            <select value={editor.metric_key} onChange={(event) => onMetricChange(event.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3">
              {catalog.map((metric) => <option key={metric.key} value={metric.key}>{metric.label}</option>)}
            </select>
          </label>
        )}
        <label className="mt-5 block text-sm font-bold text-slate-700">KPI name
          <input required maxLength={160} value={editor.name} onChange={(event) => onChange({ ...editor, name: event.target.value })} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
        </label>
        <label className="mt-5 block text-sm font-bold text-slate-700">Why this matters
          <textarea maxLength={500} rows={3} value={editor.description} onChange={(event) => onChange({ ...editor, description: event.target.value })} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
        </label>
        <div className="mt-5 grid grid-cols-2 gap-4">
          <label className="block text-sm font-bold text-slate-700">Pre-pilot baseline
            <input type="number" step="any" value={editor.baseline_value} onChange={(event) => onChange({ ...editor, baseline_value: event.target.value })} placeholder="Optional" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
          </label>
          <label className="block text-sm font-bold text-slate-700">Pilot target ({selected?.unit})
            <input required type="number" step="any" value={editor.target_value} onChange={(event) => onChange({ ...editor, target_value: event.target.value })} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
          </label>
        </div>
        <div className="mt-7 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700">Cancel</button>
          <button disabled={saving} className="rounded-xl bg-blue-700 px-5 py-3 text-sm font-bold text-white disabled:opacity-50">{saving ? "Saving…" : "Save KPI"}</button>
        </div>
      </form>
    </div>
  );
}

function formatValue(value, unit) {
  if (value === null || value === undefined) return "Not available";
  if (unit === "%") return `${value}%`;
  return `${value} ${unit}`;
}

function formatStatus(status) {
  return status ? String(status).replaceAll("_", " ").replace(/\b\w/g, (character) => character.toUpperCase()) : "Unknown";
}
